import { useEffect, useState } from 'react';
import useGameStore from '../store/gameStore';
import { baseInterestRateFor, earlyPayoffOf, investmentExitPenalty, quoteLoan } from '../lib/finance';
import {
  BALLOON_RISK_PREMIUM,
  INSURANCE_POLICY_META,
  INVESTMENT_TIER_META,
  LOAN_STRUCTURE_LABEL,
  LOAN_TERMS,
  PREPAYMENT_FEE,
} from '../types/gameTypes';
import type { InsurancePolicy, InvestmentTier, LoanStructure } from '../types/gameTypes';
import { money, percent } from '../lib/format';
import { useT } from '../i18n';

/** Quick principal presets for the loan calculator. */
const LOAN_PRESETS = [1000, 2500, 5000, 10000];
const STRUCTURES: LoanStructure[] = ['ANNUITY', 'INTEREST_ONLY', 'BALLOON'];
const TIERS: InvestmentTier[] = ['SAVINGS', 'REIT', 'VENTURE'];
const POLICIES: InsurancePolicy[] = ['BASIC', 'STANDARD', 'VIP'];

const panel = 'rounded-xl border border-slate-700/60 bg-slate-950/60 p-3';
const chip = 'rounded-full border border-slate-600/60 bg-slate-800/80 px-2 py-0.5 text-[10px] font-bold';
const btn =
  'min-h-[40px] w-full rounded-xl border-2 px-3 py-2 text-xs font-extrabold transition disabled:cursor-not-allowed disabled:border-slate-700 disabled:bg-slate-800 disabled:text-slate-500';

/** Human-readable horizon of a fund (0 rounds = fully liquid). */
const investmentHorizonLabel = (tier: InvestmentTier, t: (key: string, params?: Record<string, string | number>) => string): string => {
  const rounds = INVESTMENT_TIER_META[tier].horizonRounds;
  return rounds > 0 ? t('desk.termRounds', { rounds }) : t('desk.horizonInstant');
};

/**
 * ⚖️ Interactive Financial Desk: structured loans (annuity / interest-only / balloon)
 * priced off the macro cycle, plus the treasury funds that protect idle cash from
 * inflation drag. Every input updates the live quote immediately.
 */
const FinancialDeskModal = () => {
  const store = useGameStore();
  const t = useT();
  const { financialDeskOpen, financialDeskTab, players, currentPlayerIndex, market, loans, investments } = store;
  const active = players[currentPlayerIndex];
  const human = active && !active.isAI ? active : players[0];

  const [principal, setPrincipal] = useState(2500);
  const [term, setTerm] = useState<number>(5);
  const [structure, setStructure] = useState<LoanStructure>('ANNUITY');
  const [amounts, setAmounts] = useState<Record<InvestmentTier, number>>({ SAVINGS: 1000, REIT: 1500, VENTURE: 1000 });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') store.closeFinancialDesk();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [store]);

  if (!financialDeskOpen || !human) return null;

  const phase = market.phase;
  const baseRate = baseInterestRateFor(phase);
  const effectiveRate = structure === 'BALLOON' ? baseRate + BALLOON_RISK_PREMIUM : baseRate;
  const quote = quoteLoan(principal, effectiveRate, term, structure, market.currentRound);
  const myLoans = loans.filter((loan) => loan.playerId === human.id);
  const exposure = store.loanExposureOf(human.id) + human.debt;
  const ceiling = store.debtCeilingOf(human.id);
  const headroom = Math.max(0, ceiling - exposure);
  const investmentValue = store.investmentValueOf(human.id);
  const investedPrincipal = investments.filter((entry) => entry.playerId === human.id).reduce((sum, entry) => sum + entry.principal, 0);
  const phaseStyle =
    phase === 'BOOM'
      ? 'border-emerald-400/50 bg-emerald-500/15 text-emerald-200'
      : phase === 'CRISIS'
        ? 'border-rose-400/50 bg-rose-500/15 text-rose-200'
        : 'border-amber-400/50 bg-amber-500/15 text-amber-200';

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-slate-950/80 p-0 backdrop-blur-sm sm:items-center sm:p-3" onClick={() => store.closeFinancialDesk()}>
      <div
        className="max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-t-3xl border-2 border-amber-400/50 bg-slate-900 pb-safe shadow-2xl sm:rounded-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-slate-700/60 bg-gradient-to-r from-amber-500/20 to-slate-900 px-4 py-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300">{t('desk.subtitle')}</p>
            <h2 className="text-lg font-black text-white">{t('desk.title')}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black ${phaseStyle}`}>{phase}</span>
              <span className={chip}>{t('desk.baseRate', { rate: percent(baseRate * 100, 0) })}</span>
              <span className={chip}>{t('desk.creditLine', { value: money(ceiling) })}</span>
              <span className={chip}>{t('desk.used', { value: money(exposure) })}</span>
              <span className="rounded-full border border-emerald-400/40 bg-emerald-500/15 px-2 py-0.5 text-[10px] font-black text-emerald-200">
                {t('desk.free', { value: money(headroom) })}
              </span>
              <span className={chip}>{t('desk.cash', { value: money(human.cash) })}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => store.closeFinancialDesk()}
            className="shrink-0 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-slate-700"
          >
            {t('common.close')} / ESC
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 px-4 pt-3">
          {(['LOANS', 'INVESTMENTS', 'INSURANCE'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => store.openFinancialDesk(tab)}
              className={`min-h-[36px] rounded-xl px-4 text-xs font-extrabold transition ${
                financialDeskTab === tab ? 'bg-amber-400 text-slate-950' : 'border border-slate-600 bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {tab === 'LOANS'
                ? t('desk.tabLoans', { count: myLoans.length })
                : tab === 'INVESTMENTS'
                  ? t('desk.tabInvestments', { value: money(investmentValue) })
                  : t('desk.tabInsurance')}
            </button>
          ))}
        </div>

        <div className="space-y-3 p-4">
          {financialDeskTab === 'LOANS' && (
            <div className={panel}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('desk.loanStructuring')}</p>
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <label className="block text-xs text-slate-300">
                  <span className="font-semibold">{t('desk.principal')}</span>
                  <input
                    type="number"
                    min={200}
                    step={100}
                    value={principal}
                    onChange={(event) => setPrincipal(Math.max(0, Number(event.target.value) || 0))}
                    className="mt-1 w-full rounded-lg border border-slate-600 bg-slate-950 px-2 py-1.5 text-sm font-bold text-white"
                  />
                  <input
                    type="range"
                    min={200}
                    max={Math.max(200, Math.min(20000, Math.max(200, Math.round(headroom / 100) * 100)))}
                    step={100}
                    value={Math.min(principal, Math.max(200, headroom))}
                    onChange={(event) => setPrincipal(Number(event.target.value))}
                    className="mt-2 w-full"
                  />
                  <span className="mt-2 flex flex-wrap gap-1">
                    {LOAN_PRESETS.map((preset) => (
                      <button key={preset} type="button" onClick={() => setPrincipal(preset)} className={`${chip} hover:bg-slate-700`}>
                        {money(preset)}
                      </button>
                    ))}
                    <button type="button" onClick={() => setPrincipal(Math.floor(headroom))} className={`${chip} hover:bg-slate-700`}>
                      Max {money(Math.floor(headroom))}
                    </button>
                  </span>
                </label>

                <div className="text-xs text-slate-300">
                  <span className="font-semibold">{t('desk.term')}</span>
                  <div className="mt-1 flex gap-1.5">
                    {LOAN_TERMS.map((rounds) => (
                      <button
                        key={rounds}
                        type="button"
                        onClick={() => setTerm(rounds)}
                        className={`min-h-[34px] flex-1 rounded-lg border text-xs font-extrabold transition ${
                          term === rounds ? 'border-amber-300 bg-amber-400 text-slate-950' : 'border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700'
                        }`}
                      >
                        {t('desk.termRounds', { rounds })}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-[10px] leading-snug text-slate-400">
                    {t('desk.rateNote', {
                      rate: percent(effectiveRate * 100, 1),
                      premium: structure === 'BALLOON' ? t('desk.premiumSuffix') : '',
                    })}
                  </p>
                </div>
              </div>

              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                {STRUCTURES.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setStructure(option)}
                    className={`rounded-xl border p-2 text-left text-[11px] transition ${
                      structure === option ? 'border-amber-300 bg-amber-500/15' : 'border-slate-700/60 bg-slate-900/60 hover:bg-slate-800/60'
                    }`}
                  >
                    <span className="block font-bold text-slate-100">{LOAN_STRUCTURE_LABEL[option].label}</span>
                    <span className="mt-0.5 block leading-snug text-slate-400">{LOAN_STRUCTURE_LABEL[option].blurb}</span>
                  </button>
                ))}
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
                <div className="rounded-lg border border-slate-700/60 bg-slate-900/70 px-2 py-1.5">
                  <p className="text-[9px] uppercase tracking-widest text-slate-500">{t('desk.roundPayment')}</p>
                  <p className="font-mono text-sm font-black text-white">{money(quote.perRoundPayment)}</p>
                  <p className="text-[9px] text-slate-500">{t('common.perRound')}</p>
                </div>
                <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-2 py-1.5">
                  <p className="text-[9px] uppercase tracking-widest text-rose-300/80">{t('desk.overpay')}</p>
                  <p className="font-mono text-sm font-black text-rose-200">{money(quote.totalInterest)}</p>
                  <p className="text-[9px] text-rose-300/70">{t('desk.totalInterest')}</p>
                </div>
                <div className="rounded-lg border border-slate-700/60 bg-slate-900/70 px-2 py-1.5">
                  <p className="text-[9px] uppercase tracking-widest text-slate-500">{t('desk.maturity')}</p>
                  <p className="font-mono text-sm font-black text-white">
                    {t('common.round')} {quote.maturityRound}
                  </p>
                  <p className="text-[9px] text-slate-500">{t('desk.termRounds', { rounds: quote.termRounds })}</p>
                </div>
                <div className="rounded-lg border border-slate-700/60 bg-slate-900/70 px-2 py-1.5">
                  <p className="text-[9px] uppercase tracking-widest text-slate-500">
                    {quote.balloonDue > 0 ? t('desk.balloonDue') : t('desk.totalRepayment')}
                  </p>
                  <p className="font-mono text-sm font-black text-amber-200">
                    {money(quote.balloonDue > 0 ? quote.balloonDue : quote.totalRepayment)}
                  </p>
                  <p className="text-[9px] text-slate-500">
                    {quote.balloonDue > 0 ? t('desk.atMaturity') : t('desk.principalPlusInterest')}
                  </p>
                </div>
              </div>

              <button
                type="button"
                disabled={quote.principal <= 0 || quote.principal > headroom}
                onClick={() => store.takeStructuredLoan(human.id, quote.principal, term, structure)}
                className={`${btn} mt-3 border-emerald-400/70 bg-gradient-to-r from-emerald-600/90 to-teal-600/90 text-white hover:from-emerald-500 hover:to-teal-500`}
              >
                {quote.principal > headroom
                  ? t('desk.exceedsLine', { free: money(headroom) })
                  : t('desk.draw', { amount: money(quote.principal), structure: LOAN_STRUCTURE_LABEL[structure].label })}
              </button>
            </div>
          )}

          {financialDeskTab === 'INVESTMENTS' && (
            <>
              <div className="grid gap-2 sm:grid-cols-3">
                {TIERS.map((tier) => {
                  const meta = INVESTMENT_TIER_META[tier];
                  const position = investments.find((entry) => entry.playerId === human.id && entry.tier === tier);
                  const value = position?.value ?? 0;
                  const profit = value - (position?.principal ?? 0);
                  const heldRounds = position ? Math.max(0, market.currentRound - position.openedRound) : 0;
                  const penaltyRate = position ? investmentExitPenalty(tier, heldRounds) : 0;
                  const locked = penaltyRate > 0 && value > 0;
                  return (
                    <div key={tier} className="rounded-xl border p-2 text-[11px]" style={{ borderColor: `${meta.accent}55`, backgroundColor: `${meta.accent}12` }}>
                      <p className="font-bold text-slate-100">{meta.name}</p>
                      <p className="text-[10px] text-slate-400">{meta.label}</p>
                      <p className="mt-1 text-slate-300">
                        {t('desk.expected')} <span className="font-black" style={{ color: meta.accent }}>{percent(meta.avgYield * 100, 0)}</span>
                        {t('common.perRound')}
                      </p>
                      <p className="text-[10px] text-slate-400">{t('desk.volatility')}: {meta.volatility}</p>
                      <p className="text-[10px] text-slate-400">{t('desk.liquidity')}: {meta.liquidity}</p>
                      <p className="mt-1 rounded-lg border border-slate-700/60 bg-slate-950/70 px-2 py-1 font-mono text-[11px] text-slate-100">
                        {money(value)}
                        {position && (
                          <span className={profit >= 0 ? ' text-emerald-300' : ' text-rose-300'}>
                            {' '}
                            ({profit >= 0 ? '+' : ''}
                            {money(profit)} · {t('desk.lastRound', { pct: percent(position.lastYieldRate * 100, 1) })})
                          </span>
                        )}
                      </p>
                      <label className="mt-2 block">
                        <span className="text-[10px] text-slate-400">{t('desk.amount')}</span>
                        <input
                          type="number"
                          min={0}
                          step={100}
                          value={amounts[tier]}
                          onChange={(event) => setAmounts((prev) => ({ ...prev, [tier]: Math.max(0, Number(event.target.value) || 0) }))}
                          className="mt-0.5 w-full rounded-lg border border-slate-600 bg-slate-950 px-2 py-1 text-xs font-bold text-white"
                        />
                      </label>
                      <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                        <button
                          type="button"
                          disabled={amounts[tier] <= 0 || human.cash < amounts[tier]}
                          onClick={() => store.investCash(human.id, tier, amounts[tier])}
                          className={`${btn} border-emerald-400/70 bg-emerald-600/80 text-white hover:bg-emerald-500`}
                        >
                          {t('desk.invest')}
                        </button>
                        <button
                          type="button"
                          disabled={value <= 0 || amounts[tier] <= 0}
                          onClick={() => store.withdrawInvestment(human.id, tier, amounts[tier])}
                          className={`${btn} border-amber-400/70 bg-amber-600/80 text-white hover:bg-amber-500`}
                        >
                          {t('desk.withdraw')}
                        </button>
                      </div>
                      {locked && (
                        <p className="mt-1 text-[9px] text-amber-300">
                          {t('desk.horizonNote', {
                            horizon: investmentHorizonLabel(tier, t),
                            pct: percent(penaltyRate * 100, 1),
                            held: heldRounds,
                          })}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className={`${panel} flex flex-wrap items-center justify-between gap-2 text-xs`}>
                <span className="text-slate-400">{t('desk.treasury', { live: money(investmentValue), invested: money(investedPrincipal) })}</span>
                <span className={investmentValue - investedPrincipal >= 0 ? 'font-bold text-emerald-300' : 'font-bold text-rose-300'}>
                  {investmentValue - investedPrincipal >= 0 ? '▲' : '▼'} {money(Math.abs(investmentValue - investedPrincipal))} P/L
                </span>
                <span className="text-[10px] text-slate-500">{t('desk.fundsNote')}</span>
              </div>
            </>
          )}

          {financialDeskTab === 'INSURANCE' && (
            <>
              {/* Active policy status: name, turns remaining and covered share. */}
              <div className={`${panel} flex flex-wrap items-center justify-between gap-2 text-xs`}>
                {human.insuranceTurnsRemaining > 0 ? (
                  <>
                    <span className="font-bold text-sky-200">
                      🛡️ {t(`desk.policy.${human.insurancePolicy ?? 'STANDARD'}`)} ·{' '}
                      {t('desk.insuranceActive', { turns: human.insuranceTurnsRemaining, pct: percent((human.insuranceCoverageRate ?? 0.7) * 100, 0) })}
                    </span>
                    {human.insurancePolicy === 'VIP' && (
                      <span className="rounded-full border border-amber-400/50 bg-amber-500/15 px-2 py-0.5 text-[10px] font-black text-amber-200">
                        {t('desk.insuranceImmunity')}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-slate-400">{t('desk.insuranceNone')}</span>
                )}
              </div>

              {/* Interactive comparison cards, one per policy tier. */}
              <div className="grid gap-2 sm:grid-cols-3">
                {POLICIES.map((policy) => {
                  const meta = INSURANCE_POLICY_META[policy];
                  const premium = Math.round(store.netWorthOf(human.id) * meta.premiumPct);
                  const isActive = human.insurancePolicy === policy && human.insuranceTurnsRemaining > 0;
                  const blocked = isActive || human.insuranceTurnsRemaining > 0 || premium <= 0 || human.cash < premium;
                  return (
                    <div
                      key={policy}
                      className="rounded-xl border p-2 text-[11px]"
                      style={{ borderColor: `${meta.accent}66`, backgroundColor: `${meta.accent}0d` }}
                    >
                      <p className="text-xs font-black" style={{ color: meta.accent }}>{t(`desk.policy.${policy}`)}</p>
                      <p className="mt-1 text-slate-300">{t('desk.insurancePremium', { price: money(premium), pct: percent(meta.premiumPct * 100, 0) })}</p>
                      <p className="text-slate-400">{t('desk.insuranceTerm', { turns: meta.turns })}</p>
                      <p className="text-slate-400">{t('desk.insuranceCoverage', { pct: percent(meta.coverage * 100, 0) })}</p>
                      {meta.takeoverImmunity && <p className="font-bold text-amber-300">👑 {t('desk.insuranceImmunity')}</p>}
                      <p className="mt-1 text-[10px] italic text-slate-500">{t(`desk.policy.${policy}.note`)}</p>
                      <button
                        type="button"
                        disabled={blocked}
                        onClick={() => store.buyInsurance(human.id, policy)}
                        className={`${btn} mt-2 border-sky-400/70 bg-sky-600/80 text-white hover:bg-sky-500`}
                      >
                        {isActive ? t('desk.insuranceActiveCta') : t('desk.insuranceActivate')}
                      </button>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-500">{t('desk.insuranceNote')}</p>
            </>
          )}

          {myLoans.length > 0 && (
            <div className={panel}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                {t('desk.activeLoans', { outstanding: money(store.loanExposureOf(human.id)) })}
              </p>
              <div className="mt-2 space-y-1.5">
                {myLoans.map((loan) => {
                  const accrued =
                    loan.structure === 'BALLOON'
                      ? Math.max(0, loan.principal * Math.pow(1 + loan.ratePerRound, loan.termRounds - loan.roundsRemaining) - loan.outstanding)
                      : 0;
                  const payoff = earlyPayoffOf(loan.outstanding, accrued).total;
                  const fee = payoff - Math.round(loan.outstanding);
                  return (
                    <div key={loan.id} className="rounded-lg border border-slate-700/60 bg-slate-900/60 px-2 py-1.5 text-[11px]">
                      <p className="flex flex-wrap items-center justify-between gap-1">
                        <span className="font-bold text-slate-100">{LOAN_STRUCTURE_LABEL[loan.structure].label}</span>
                        <span className="font-mono text-slate-300">
                          {money(loan.principal)} @ {percent(loan.ratePerRound * 100, 1)}/round
                        </span>
                      </p>
                      <p className="text-slate-400">
                        {loan.perRoundPayment > 0 ? t('desk.perRound', { amount: money(loan.perRoundPayment) }) : t('desk.zeroCoupon')} ·{' '}
                        {t('desk.outstanding', { value: money(loan.outstanding) })} · {t('desk.roundsLeft', { rounds: loan.roundsRemaining })} ·{' '}
                        {t('desk.matures', { round: loan.maturityRound })}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[10px] text-slate-500">
                          {t('desk.prepayToday', {
                            total: money(payoff),
                            fee: money(fee),
                            pct: percent(PREPAYMENT_FEE * 100, 1),
                          })}
                        </span>
                        <button
                          type="button"
                          disabled={human.cash < payoff}
                          onClick={() => store.repayLoanEarly(loan.id)}
                          className="min-h-[28px] rounded-lg border border-emerald-400/60 bg-emerald-600/80 px-2 py-0.5 text-[10px] font-extrabold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:border-slate-700 disabled:bg-slate-800 disabled:text-slate-500"
                        >
                          {t('desk.prepayEarly')}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinancialDeskModal;

